import React, { useState, useEffect } from "react";
import HubHeader from "../HubHeader";
import HubTaskbar from "../HubTaskbar";
import { useTheme } from "../../../contexts/ThemeContext";
import "../../../assets/css/Hub.css";
import "../../../assets/css/HubSettings.css";

type SettingsTab = "general" | "notifications" | "personalization" | "account";

export default function HubSettings() {
  const [activeTab, setActiveTab] = useState<SettingsTab>("general");
  const { theme, accentColor, setTheme, setAccentColor } = useTheme();
  const [tempColor, setTempColor] = useState<string>(accentColor);
  const [showColorPicker, setShowColorPicker] = useState(false);

  useEffect(() => {
    setTempColor(accentColor);
  }, [accentColor]);

  return (
    <div className="hub-settings-layout">
      <HubHeader />
      
      <div className="hub-settings-body">
        <aside className="hub-settings-sidebar-wrap taskbar-only" aria-label="Navigation">
          <HubTaskbar />
        </aside>
        
        <main className="hub-settings-main-content">
          <div className="hub-settings-inner">
            <div className="hub-settings-header">
              <h1 className="hub-settings-title">Settings</h1>
              <p className="hub-settings-subtitle">
                Manage your account settings and preferences
              </p>
            </div>

            <div className="hub-settings-container">
              <nav className="hub-settings-tabs" role="tablist" aria-label="Settings sections">
                <button
                  className={`hub-settings-tab ${activeTab === "general" ? "active" : ""}`}
                  onClick={() => setActiveTab("general")}
                  role="tab"
                  aria-selected={activeTab === "general"}
                  aria-controls="general-panel"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M12 1v6m0 6v6m5.2-13.2l-4.2 4.2m-2 2l-4.2 4.2m13.2-5.2h-6m-6 0H1m18.2 5.2l-4.2-4.2m-2-2l-4.2-4.2" />
                  </svg>
                  General
                </button>
                <button
                  className={`hub-settings-tab ${activeTab === "notifications" ? "active" : ""}`}
                  onClick={() => setActiveTab("notifications")}
                  role="tab"
                  aria-selected={activeTab === "notifications"}
                  aria-controls="notifications-panel"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                  Notifications
                </button>
                <button
                  className={`hub-settings-tab ${activeTab === "personalization" ? "active" : ""}`}
                  onClick={() => setActiveTab("personalization")}
                  role="tab"
                  aria-selected={activeTab === "personalization"}
                  aria-controls="personalization-panel"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
                  </svg>
                  Personalization
                </button>
                <button
                  className={`hub-settings-tab ${activeTab === "account" ? "active" : ""}`}
                  onClick={() => setActiveTab("account")}
                  role="tab"
                  aria-selected={activeTab === "account"}
                  aria-controls="account-panel"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  Account
                </button>
              </nav>

              <div className="hub-settings-content">
                {activeTab === "general" && (
                  <div id="general-panel" role="tabpanel" aria-labelledby="general-tab" className="hub-settings-panel">
                    <h2 className="hub-settings-section-title">General Settings</h2>
                    
                    <div className="hub-settings-group">
                      <label className="hub-settings-label">
                        Language
                        <select className="hub-settings-select">
                          <option value="en">English</option>
                          <option value="es">Spanish</option>
                          <option value="fr">French</option>
                          <option value="de">German</option>
                        </select>
                      </label>
                    </div>

                    <div className="hub-settings-group">
                      <label className="hub-settings-label">
                        Timezone
                        <select className="hub-settings-select">
                          <option value="utc">UTC</option>
                          <option value="est">Eastern Time</option>
                          <option value="pst">Pacific Time</option>
                          <option value="cet">Central European Time</option>
                        </select>
                      </label>
                    </div>

                    <div className="hub-settings-group">
                      <div className="hub-settings-toggle-row">
                        <div>
                          <div className="hub-settings-toggle-label">Auto-save conversations</div>
                          <div className="hub-settings-toggle-description">
                            Automatically save your chat conversations
                          </div>
                        </div>
                        <label className="hub-toggle-switch">
                          <input type="checkbox" defaultChecked />
                          <span className="hub-toggle-slider"></span>
                        </label>
                      </div>
                    </div>

                    <div className="hub-settings-group">
                      <div className="hub-settings-toggle-row">
                        <div>
                          <div className="hub-settings-toggle-label">Analytics</div>
                          <div className="hub-settings-toggle-description">
                            Help improve Momentum by sharing usage data
                          </div>
                        </div>
                        <label className="hub-toggle-switch">
                          <input type="checkbox" defaultChecked />
                          <span className="hub-toggle-slider"></span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "notifications" && (
                  <div id="notifications-panel" role="tabpanel" aria-labelledby="notifications-tab" className="hub-settings-panel">
                    <h2 className="hub-settings-section-title">Notification Preferences</h2>
                    
                    <div className="hub-settings-group">
                      <div className="hub-settings-toggle-row">
                        <div>
                          <div className="hub-settings-toggle-label">Email notifications</div>
                          <div className="hub-settings-toggle-description">
                            Receive updates and alerts via email
                          </div>
                        </div>
                        <label className="hub-toggle-switch">
                          <input type="checkbox" defaultChecked />
                          <span className="hub-toggle-slider"></span>
                        </label>
                      </div>
                    </div>

                    <div className="hub-settings-group">
                      <div className="hub-settings-toggle-row">
                        <div>
                          <div className="hub-settings-toggle-label">Push notifications</div>
                          <div className="hub-settings-toggle-description">
                            Get browser notifications for important updates
                          </div>
                        </div>
                        <label className="hub-toggle-switch">
                          <input type="checkbox" />
                          <span className="hub-toggle-slider"></span>
                        </label>
                      </div>
                    </div>

                    <div className="hub-settings-group">
                      <div className="hub-settings-toggle-row">
                        <div>
                          <div className="hub-settings-toggle-label">AMP execution alerts</div>
                          <div className="hub-settings-toggle-description">
                            Notify when AMP executions complete
                          </div>
                        </div>
                        <label className="hub-toggle-switch">
                          <input type="checkbox" defaultChecked />
                          <span className="hub-toggle-slider"></span>
                        </label>
                      </div>
                    </div>

                    <div className="hub-settings-group">
                      <div className="hub-settings-toggle-row">
                        <div>
                          <div className="hub-settings-toggle-label">Weekly summary</div>
                          <div className="hub-settings-toggle-description">
                            Receive weekly usage summaries
                          </div>
                        </div>
                        <label className="hub-toggle-switch">
                          <input type="checkbox" defaultChecked />
                          <span className="hub-toggle-slider"></span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "personalization" && (
                  <div id="personalization-panel" role="tabpanel" aria-labelledby="personalization-tab" className="hub-settings-panel">
                    <h2 className="hub-settings-section-title">Personalization</h2>

                    <div className="hub-settings-group">
                      <label className="hub-settings-label">
                        Accent color
                        <div className="hub-color-picker">
                          <div 
                            className={`hub-color-option ${accentColor === '#6b46c1' ? 'active' : ''}`}
                            style={{ background: '#6b46c1' }}
                            onClick={() => setAccentColor('#6b46c1')}
                          ></div>
                          <div 
                            className={`hub-color-option ${accentColor === '#3b82f6' ? 'active' : ''}`}
                            style={{ background: '#3b82f6' }}
                            onClick={() => setAccentColor('#3b82f6')}
                          ></div>
                          <div 
                            className={`hub-color-option ${accentColor === '#10b981' ? 'active' : ''}`}
                            style={{ background: '#10b981' }}
                            onClick={() => setAccentColor('#10b981')}
                          ></div>
                          <div 
                            className={`hub-color-option ${accentColor === '#f59e0b' ? 'active' : ''}`}
                            style={{ background: '#f59e0b' }}
                            onClick={() => setAccentColor('#f59e0b')}
                          ></div>
                          <div 
                            className={`hub-color-option ${accentColor === '#ef4444' ? 'active' : ''}`}
                            style={{ background: '#ef4444' }}
                            onClick={() => setAccentColor('#ef4444')}
                          ></div>
                          <div 
                            className={`hub-color-option ${accentColor === '#ec4899' ? 'active' : ''}`}
                            style={{ background: '#ec4899' }}
                            onClick={() => setAccentColor('#ec4899')}
                          ></div>
                          <button 
                            type="button"
                            className="hub-color-option hub-color-option-custom"
                            style={{ 
                              background: `linear-gradient(135deg, ${tempColor} 0%, ${tempColor}dd 100%)`,
                              border: '2px dashed rgba(var(--accent-color-rgb), 0.4)',
                              position: 'relative',
                              cursor: 'pointer'
                            }}
                            title="Custom color"
                            onClick={(e) => {
                              e.preventDefault();
                              console.log('Custom color clicked, current state:', showColorPicker);
                              setShowColorPicker(!showColorPicker);
                            }}
                          >
                            <svg 
                              width="16" 
                              height="16" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              stroke="white" 
                              strokeWidth="2"
                              style={{ pointerEvents: 'none', position: 'relative', zIndex: 1 }}
                            >
                              <path d="M12 5v14M5 12h14"/>
                            </svg>
                          </button>
                        </div>
                        {showColorPicker && (
                          <div className="hub-custom-color-picker">
                            <div className="hub-color-picker-header">
                              <span>Choose custom color</span>
                              <button 
                                className="hub-color-picker-close"
                                onClick={() => setShowColorPicker(false)}
                              >
                                ×
                              </button>
                            </div>
                            <div className="hub-color-picker-row">
                              <input
                                type="color"
                                value={tempColor}
                                onChange={(e) => setTempColor(e.target.value)}
                                className="hub-color-picker-input"
                              />
                              <div className="hub-color-picker-preview" style={{ background: tempColor }}>
                                <span>Preview</span>
                              </div>
                              <button 
                                className="hub-color-picker-apply"
                                onClick={() => {
                                  setAccentColor(tempColor);
                                  setShowColorPicker(false);
                                }}
                              >
                                Apply
                              </button>
                            </div>
                          </div>
                        )}
                      </label>
                    </div>

                    <div className="hub-settings-group">
                      <label className="hub-settings-label">
                        Font size
                        <select className="hub-settings-select">
                          <option value="small">Small</option>
                          <option value="medium" selected>Medium</option>
                          <option value="large">Large</option>
                        </select>
                      </label>
                    </div>

                    <div className="hub-settings-group">
                      <div className="hub-settings-toggle-row">
                        <div>
                          <div className="hub-settings-toggle-label">Compact mode</div>
                          <div className="hub-settings-toggle-description">
                            Show more content with reduced spacing
                          </div>
                        </div>
                        <label className="hub-toggle-switch">
                          <input type="checkbox" />
                          <span className="hub-toggle-slider"></span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "account" && (
                  <div id="account-panel" role="tabpanel" aria-labelledby="account-tab" className="hub-settings-panel">
                    <h2 className="hub-settings-section-title">Account Information</h2>
                    
                    <div className="hub-settings-group">
                      <label className="hub-settings-label">
                        Full name
                        <input type="text" className="hub-settings-input" placeholder="Enter your full name" defaultValue="John Doe" />
                      </label>
                    </div>

                    <div className="hub-settings-group">
                      <label className="hub-settings-label">
                        Email address
                        <input type="email" className="hub-settings-input" placeholder="Enter your email" defaultValue="john.doe@example.com" />
                      </label>
                    </div>

                    <div className="hub-settings-group">
                      <label className="hub-settings-label">
                        Company
                        <input type="text" className="hub-settings-input" placeholder="Enter your company name" defaultValue="Acme Inc." />
                      </label>
                    </div>

                    <div className="hub-settings-divider"></div>

                    <h3 className="hub-settings-subsection-title">Security</h3>

                    <div className="hub-settings-group">
                      <button className="hub-settings-button secondary">
                        Change Password
                      </button>
                    </div>

                    <div className="hub-settings-group">
                      <div className="hub-settings-toggle-row">
                        <div>
                          <div className="hub-settings-toggle-label">Two-factor authentication</div>
                          <div className="hub-settings-toggle-description">
                            Add an extra layer of security to your account
                          </div>
                        </div>
                        <label className="hub-toggle-switch">
                          <input type="checkbox" />
                          <span className="hub-toggle-slider"></span>
                        </label>
                      </div>
                    </div>

                    <div className="hub-settings-divider"></div>

                    <h3 className="hub-settings-subsection-title">Danger Zone</h3>

                    <div className="hub-settings-group">
                      <button className="hub-settings-button danger">
                        Delete Account
                      </button>
                      <p className="hub-settings-help-text">
                        This action cannot be undone. All your data will be permanently deleted.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="hub-settings-footer">
              <button className="hub-settings-button primary">
                Save Changes
              </button>
              <button className="hub-settings-button secondary">
                Cancel
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
